import {
  applySerializedTrialForm,
  debounce,
  loadTrialForm,
  saveTrialForm,
  serializeTrialForm,
  SOCIAL_TRIAL_SESSION_KEY
} from './lib/trialFormSession';
import { initTaxNumberInputs } from './taxNumberInput';
import { parseInputNumber } from './lib/formParse';
import { setYenWithZeroClass, setHidden } from './lib/taxDomHelpers';
import {
  readDobFromBasicInfo,
  readYearFromBasicInfo,
  readMonthFromBasicInfo,
} from './lib/basicInfoReader';
import { calculate社会保険料 } from '@api-lib/socialCalc';
import type {
  社会保険入力データ,
  健康保険被保険者区分,
  年金被保険者区分,
  事業種別,
} from '@api-lib/socialCalc';

/** BasicInfo フォームを検証して Social 計算に必要な値を返す */
type BasicInfoValid =
  | {
      ok: true;
      対象年: number;
      対象月: number;
      生年月日_年: number;
      生年月日_月: number;
      生年月日_日: number;
    }
  | { ok: false; message: string };

function validateBasicInfoMenuForSocial(): BasicInfoValid {
  const yearResult = readYearFromBasicInfo();
  if (typeof yearResult === 'string') return { ok: false, message: yearResult };

  const monthResult = readMonthFromBasicInfo();
  if (typeof monthResult === 'string') return { ok: false, message: monthResult };

  const dob = readDobFromBasicInfo();
  if (!dob.ok) return { ok: false, message: dob.message };

  return {
    ok: true,
    対象年: yearResult,
    対象月: monthResult,
    生年月日_年: dob.y,
    生年月日_月: dob.m,
    生年月日_日: dob.d,
  };
}

function syncFields(root: HTMLElement): void {
  const kenpoType = root.querySelector<HTMLSelectElement>('#si-kenpo-type')?.value as 健康保険被保険者区分;
  const nenkinType = root.querySelector<HTMLSelectElement>('#si-nenkin-type')?.value as 年金被保険者区分;
  const boss = root.querySelector<HTMLInputElement>('#si-koyo-boss')?.checked ?? false;
  const qualify = root.querySelector<HTMLInputElement>('#si-koyo-qualify')?.checked ?? true;
  const rosaiJoin = root.querySelector<HTMLInputElement>('#si-rosai-join')?.checked ?? false;

  const isEmployee健保 = kenpoType === '協会けんぽ' || kenpoType === '健康保険組合';
  const isKokuho = kenpoType === '国民健康保険' || kenpoType === '国民健康保険組合';
  const isEmployee年金 = nenkinType === '会社員';
  const showKokunen = nenkinType === '自営業者';
  const showKoyo = !boss && qualify;

  // 健康保険
  setHidden(root, '#si-hyojun-field',      !isEmployee健保);
  setHidden(root, '#si-kenpo-rate-field',  !isEmployee健保);
  setHidden(root, '#si-income-field',      !isKokuho);
  setHidden(root, '#si-kokuho-rate-field', !isKokuho);
  setHidden(root, '#si-kokuho-equal-field',!isKokuho);

  // 年金
  setHidden(root, '#si-kosei-rate-field', !isEmployee年金);
  setHidden(root, '#si-kokunen-field',    !showKokunen);

  // 雇用保険
  setHidden(root, '#si-koyo-pay-field',  !showKoyo);
  setHidden(root, '#si-koyo-type-field', !showKoyo);

  // 介護保険の詳細（協会けんぽ/健保組合のときだけ第2号料率を表示）
  setHidden(root, '#si-kaigo-rate-field', !isEmployee健保);

  // 労災保険
  setHidden(root, '#si-rosai-daily-field', !rosaiJoin);
  setHidden(root, '#si-rosai-rate-field',  !rosaiJoin);
}

export function initSocialInsurancePage(root: HTMLElement): void {
  initTaxNumberInputs(root);
  const basicInfo = document.getElementById('basic-info-menu');
  if (basicInfo) initTaxNumberInputs(basicInfo);

  const form = root.querySelector<HTMLFormElement>('#social-main-form');
  if (!form) return;

  const errEl = root.querySelector<HTMLElement>('#social-error');
  const showErr = (msg: string): void => {
    if (errEl) {
      errEl.textContent = msg;
      errEl.hidden = false;
    }
  };
  const hideErr = (): void => {
    if (errEl) errEl.hidden = true;
  };

  const persistSocialDraft = (): void => {
    const prev = loadTrialForm(SOCIAL_TRIAL_SESSION_KEY);
    saveTrialForm(SOCIAL_TRIAL_SESSION_KEY, {
      v: 1,
      fields: serializeTrialForm(form),
      hasCalculated: prev?.hasCalculated ?? false
    });
  };
  const debouncedPersistSocial = debounce(persistSocialDraft, 400);

  const socialPersisted = loadTrialForm(SOCIAL_TRIAL_SESSION_KEY);
  if (socialPersisted) {
    applySerializedTrialForm(socialPersisted.fields);
  }

  const onSocialFormChange = (): void => {
    syncFields(root);
    debouncedPersistSocial();
  };
  form.addEventListener('change', onSocialFormChange);
  form.addEventListener('input', debouncedPersistSocial);
  syncFields(root);

  const runSocialCalculation = (): boolean => {
    const basic = validateBasicInfoMenuForSocial();
    if (!basic.ok) {
      showErr(basic.message);
      return false;
    }
    hideErr();

    const getInput  = (id: string) => form.querySelector<HTMLInputElement>(id);
    const getSelect = (id: string) => form.querySelector<HTMLSelectElement>(id);

    const kenpoType = (getSelect('#si-kenpo-type')?.value ?? '協会けんぽ') as 健康保険被保険者区分;
    const nenkinType = (getSelect('#si-nenkin-type')?.value ?? '会社員') as 年金被保険者区分;
    const isEmployee健保 = kenpoType === '協会けんぽ' || kenpoType === '健康保険組合';

    const 標準報酬月額 = isEmployee健保 ? parseInputNumber(getInput('#si-hyojun'), 0) : 0;
    const 前年の合計所得 = parseInputNumber(getInput('#si-income'), 0);

    const input: 社会保険入力データ = {
      対象年:     basic.対象年,
      対象月:     basic.対象月,
      生年月日_年: basic.生年月日_年,
      生年月日_月: basic.生年月日_月,
      生年月日_日: basic.生年月日_日,

      健康保険被保険者区分: kenpoType,
      年金被保険者区分:     nenkinType,

      標準報酬月額,
      前年の合計所得,

      年金保険情報: {
        厚生年金本人負担率: parseInputNumber(getInput('#si-kosei-rate'), 0.0915),
        国民年金月額:       parseInputNumber(getInput('#si-kokunen'),    16980),
      },

      健康保険情報: {
        健康保険料率:   parseInputNumber(getInput('#si-kenpo-rate'),   0.10),
        国保_均等割額: parseInputNumber(getInput('#si-kokuho-equal'), 52800),
        国保_所得割率: parseInputNumber(getInput('#si-kokuho-rate'),  0.10),
      },

      介護保険情報: {
        医療保険に加入しているか:            getInput('#si-kaigo-med')?.checked    ?? true,
        判定月の月末時点で日本に住民票があるか: getInput('#si-kaigo-jumin')?.checked  ?? true,
        適用除外施設に入所しているか:          getInput('#si-kaigo-except')?.checked ?? false,
        標準報酬月額,
        労使折半する前の介護保険料率: parseInputNumber(getInput('#si-kaigo-rate'), 0.0182),
        市区町村ごとの基準額:         parseInputNumber(getInput('#si-kaigo-base'), 6000),
        所得段階ごとの乗数:           parseInputNumber(getInput('#si-kaigo-mult'), 1.0),
      },

      雇用保険情報: {
        社長や役員等のような雇う側かどうか:            getInput('#si-koyo-boss')?.checked     ?? false,
        is週20時間以上かつ31日以上の雇用見込み:        getInput('#si-koyo-qualify')?.checked  ?? true,
        残業代_通勤交通費_各種手当などをすべて足した対象月の実際の総支給額:
          parseInputNumber(getInput('#si-koyo-pay'), 0),
        事業種別: (getSelect('#si-koyo-type')?.value ?? '一般') as 事業種別,
      },

      労災保険情報: {
        特別加入制度に加入している: getInput('#si-rosai-join')?.checked ?? false,
        給付基礎日額:               parseInputNumber(getInput('#si-rosai-daily'), 0),
        労災保険料:                 parseInputNumber(getInput('#si-rosai-rate'),  0),
      },
    };

    const result = calculate社会保険料(input);

    setYenWithZeroClass(root.querySelector<HTMLElement>('#si-out-kenpo'), result.月間健康保険料);
    setYenWithZeroClass(root.querySelector<HTMLElement>('#si-out-nenkin'), result.月間年金保険料);
    setYenWithZeroClass(root.querySelector<HTMLElement>('#si-out-kaigo'), result.月間介護保険料);
    setYenWithZeroClass(root.querySelector<HTMLElement>('#si-out-koyo'), result.月間雇用保険料);
    setYenWithZeroClass(root.querySelector<HTMLElement>('#si-out-kosodate'), result.月間子育て支援金);
    setYenWithZeroClass(root.querySelector<HTMLElement>('#si-out-total-month'), result.月間本人負担合計);
    setYenWithZeroClass(root.querySelector<HTMLElement>('#si-out-rosai'), result.年間労災保険料);

    const grossPay = parseInputNumber(getInput('#si-koyo-pay'), 0);
    const takeHomeRow = root.querySelector<HTMLElement>('#si-out-takehome-row');
    const takeHomeEl = root.querySelector<HTMLElement>('#si-out-takehome');
    const takeHint = root.querySelector<HTMLElement>('#si-takehome-hint');
    if (grossPay > 0 && takeHomeRow && takeHomeEl) {
      setYenWithZeroClass(takeHomeEl, grossPay - result.月間本人負担合計);
      takeHomeRow.hidden = false;
      if (takeHint) takeHint.hidden = true;
    } else {
      if (takeHomeRow) takeHomeRow.hidden = true;
      if (takeHint) takeHint.hidden = false;
    }

    setHidden(root, '#si-out-rosai-row',    result.年間労災保険料 === 0);
    setHidden(root, '#si-out-kosodate-row', result.月間子育て支援金 === 0);

    setHidden(root, '#si-placeholder', true);
    setHidden(root, '#si-result',      false);
    return true;
  };

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (runSocialCalculation()) {
      saveTrialForm(SOCIAL_TRIAL_SESSION_KEY, {
        v: 1,
        fields: serializeTrialForm(form),
        hasCalculated: true
      });
    }
  });

  if (socialPersisted?.hasCalculated) {
    runSocialCalculation();
  }
}
